import { useMemo, useState } from 'react';
import { Box, Button, Card, CardActionArea, CardContent, CircularProgress, Stack, ThemeProvider, Typography } from '@mui/material';

import { getTemplatesForUnitType } from '@/lib/templates/catalog';
import type { SiteTemplate } from '@/lib/templates/types';
import { createUnitSiteTheme, resolveButtonFontKey, useGoogleFonts } from '@/styles/theme/create-unit-site-theme';
import { BlockRenderer } from '@/components/blocks/BlockRenderer';
import type { Block, UnitType } from '@/types/database';

interface TemplateGalleryProps {
  unitType: UnitType | null | undefined;
  unitName: string;
  unitIdentifier: string;
  applying: boolean;
  onSelectTemplate: (template: SiteTemplate) => void;
}

export function TemplateGallery({ unitType, unitName, unitIdentifier, applying, onSelectTemplate }: TemplateGalleryProps) {
  const templates = useMemo(() => getTemplatesForUnitType(unitType), [unitType]);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const previewUnitName = unitName || 'Your Unit';

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Choose a starting point
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Pick a template to get your site started — every page and color can be customized afterward.
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            unitName={previewUnitName}
            unitIdentifier={unitIdentifier}
            disabled={applying}
            loading={applying && pendingId === template.id}
            onSelect={() => {
              setPendingId(template.id);
              onSelectTemplate(template);
            }}
          />
        ))}
      </Box>
    </Stack>
  );
}

interface TemplateCardProps {
  template: SiteTemplate;
  unitName: string;
  unitIdentifier: string;
  disabled: boolean;
  loading: boolean;
  onSelect: () => void;
}

function TemplateCard({ template, unitName, unitIdentifier, disabled, loading, onSelect }: TemplateCardProps) {
  const theme = useMemo(() => createUnitSiteTheme(template.theme), [template.theme]);
  useGoogleFonts([template.theme.fonts.headingFont, template.theme.fonts.paragraphFont, resolveButtonFontKey(template.theme.fonts), template.theme.fonts.miscFont]);

  const previewBlock: Block | null = useMemo(() => {
    const pages = template.buildPages(unitName, unitIdentifier);
    const firstBlock = pages[0]?.blocks[0];
    if (!firstBlock) return null;
    return {
      id: `${template.id}-preview`,
      page_id: 'preview',
      block_type: firstBlock.block_type,
      content: firstBlock.content as unknown as Block['content'],
      block_order: firstBlock.block_order,
      row_order: firstBlock.block_order,
      col_span: 12,
      visibility: 'public',
      created_at: '',
      updated_at: '',
    };
  }, [template, unitName, unitIdentifier]);

  return (
    <Card variant="outlined">
      <CardActionArea onClick={onSelect} disabled={disabled}>
        <Box sx={{ height: 140, overflow: 'hidden', pointerEvents: 'none' }}>
          <ThemeProvider theme={theme}>
            <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'top left', width: `${100 / 0.7}%` }}>
              {previewBlock ? <BlockRenderer block={previewBlock} basePath="" /> : null}
            </Box>
          </ThemeProvider>
        </Box>
        <CardContent>
          <Typography sx={{ fontWeight: 700 }}>{template.name}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {template.description}
          </Typography>
          {loading ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Applying...</Typography>
            </Stack>
          ) : (
            <Button size="small" variant="outlined" disabled={disabled}>
              Use this template
            </Button>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
